import axios from 'axios';
import { API_BASE } from '../config';

// Candidate-portal API client. Separate JWT universe from any ERP staff auth — the token here is a
// portal token minted by /public/otp/verify and only unlocks /public/me + /public/applications.
// Stored in localStorage so a half-finished application survives an accidental reload/back.
const TOKEN_KEY = 'adel_portal_token';

export function getPortalToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setPortalToken(token) {
  try { token ? localStorage.setItem(TOKEN_KEY, token) : localStorage.removeItem(TOKEN_KEY); } catch { /* private mode */ }
}
export function clearPortalToken() { setPortalToken(null); }

export const client = axios.create({ baseURL: API_BASE, timeout: 30000 });
client.interceptors.request.use((config) => {
  const t = getPortalToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Single-active-session (2026-07-28): the backend invalidates all prior sessions when the account
// signs in elsewhere. When THIS session is the superseded one, any authed call returns 401 with
// code 'SESSION_SUPERSEDED' — clear the dead token and bounce to /login with a notice. Scoped to
// that exact code so ordinary 401s (wrong password, natural expiry) still surface their own message
// and the login screen keeps working.
client.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error?.response?.data?.code === 'SESSION_SUPERSEDED') {
      clearPortalToken();
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.assign('/login?loggedout=1');
      }
    }
    return Promise.reject(error);
  }
);

// Normalise an axios error into a plain message the UI can show a non-technical user.
export function portalErrorMessage(err, fallback = 'Something went wrong. Please try again.') {
  if (err?.response?.data?.error) return err.response.data.error;
  if (err?.message === 'Network Error') return 'Network problem — check your connection and try again.';
  return fallback;
}

// ── OTP ────────────────────────────────────────────────────────────────────
// Every OTP send goes through RegisterGate now (registration AND sign-in both — 2026-07-25), so this
// always carries a captchaToken (empty string in dev — captcha.ts's own server-side dev fallback
// accepts that when RECAPTCHA_SECRET_KEY is unset). There is deliberately no captcha-less variant.
export async function requestOtpWithCaptcha(email, captchaToken) {
  const { data } = await client.post('/public/otp/request', { email, captchaToken });
  return data;
}

export async function verifyOtp(email, code) {
  const { data } = await client.post('/public/otp/verify', { email, code });
  setPortalToken(data.token);
  return data.account; // { id, email, name, phone }
}

// ── Password (optional, added after first OTP verify — OTP itself never goes away) ───────────
export async function setPassword(password) {
  const { data } = await client.post('/public/password/set', { password });
  return data; // { ok: true }
}

export async function loginWithPassword(email, password) {
  const { data } = await client.post('/public/login/password', { email, password });
  setPortalToken(data.token);
  return data.account;
}

// ── Quick apply (already-registered candidate, second+ job from the public listing) ──────────
// Reuses the candidate's existing CV/passport server-side — no re-upload, no wizard. The listing
// UI stays deliberately simple either way: Apply -> Applied, nothing else shown (owner correction,
// 2026-07-25) — the profession-cap logic all happens server-side.
export async function quickApply(slug) {
  const { data } = await client.post(`/public/jobs/${slug}/quick-apply`, {});
  return data; // { ok, applied } or throws 409 NOT_REGISTERED if registration isn't complete yet
}

// ── Account ────────────────────────────────────────────────────────────────
export async function getMe() {
  const { data } = await client.get('/public/me');
  return data.account;
}

export async function updateMe({ name, firstName, lastName, phone }) {
  const { data } = await client.put('/public/me', { name, firstName, lastName, phone });
  return data.account;
}

// GET /public/applications — this account's application history across every profile it has
// submitted with. `job` is null on a general "send your CV" submission with no specific demand.
export async function fetchMyApplications() {
  const { data } = await client.get('/public/applications');
  return data.applications; // [{ id, status, statusLabel, appliedAt, job:{title,slug,country}|null }]
}

// ── Document staging (each file is normalised to PDF server-side) ────────────
// type ∈ 'cv' | 'passport' | 'education' | 'experience' | 'other'.
// Returns { stageId, type, pageCount, thumbnails:[dataURI…] }.
export async function stageDoc(file, type) {
  const form = new FormData();
  form.append('file', file);
  form.append('type', type);
  const { data } = await client.post('/public/documents/stage', form, { timeout: 120000 });
  return data;
}

// Keep exactly one passport page. Returns { stageId, pageCount:1 }.
export async function pickPassportPage(stageId, pageIndex) {
  const { data } = await client.post('/public/documents/passport-page', { stageId, pageIndex });
  return data;
}

export async function removeStaged(stageId) {
  try { await client.delete(`/public/documents/${stageId}`); } catch { /* best-effort */ }
}

// Preview needs the bearer token, so an <iframe src> can't load it directly — fetch as a blob and
// hand back an object URL. Caller must URL.revokeObjectURL when done.
export async function fetchPreviewObjectUrl(stageId) {
  const { data } = await client.get(`/public/documents/${stageId}/preview`, { responseType: 'blob' });
  return URL.createObjectURL(data);
}

// ── Split a combined file (Phase 2) ──────────────────────────────────────────
export async function splitDoc(file) {
  const form = new FormData();
  form.append('file', file);
  const { data } = await client.post('/public/documents/split', form, { timeout: 120000 });
  return data; // { splitId, pages:[{ index, thumbnail }] }
}
export async function assembleSplit(splitId, assignments) {
  const { data } = await client.post('/public/documents/assemble', { splitId, assignments });
  return data; // { docs:[{ stageId, type, pageCount }] }
}

// ── Professions (for the general, no-job apply flow's profession step) ───────
// Fully public — no portal token required (the interceptor attaches one opportunistically if
// present, but this route doesn't need it).
export async function fetchProfessions() {
  const { data } = await client.get('/public/professions');
  return data.professions; // [{ id, name }]
}
// NOTE: the returning-applicant profession step reads/writes the on-file profile via the EXISTING
// api/profiles.js helpers (fetchProfiles / updateProfile) — no portal.js duplicate (S5).

// ── Application ──────────────────────────────────────────────────────────────
// GET /public/apply-documents — the required document set the server enforces for a GENERAL
// (no-demand) "Send your CV" application: the built-in defaults ∪ the admin's mandatoryForAll
// DocumentTypes, each { slot, name, requirement } (mandatory-first). `slot` is a built-in key
// ('cv'|'passport'|…) or `dt:<id>`. Same shape as a demand's job.documents, so buildDemandSlots can
// consume it directly — letting the general flow collect every required doc in ONE screen (no submit
// 400 on the mandatoryForAll docs the hardcoded SLOTS omit → no second document screen).
export async function getApplyDocuments() {
  const { data } = await client.get('/public/apply-documents');
  return data.documents; // [{ slot, name, requirement }]
}

// docs: [{ stageId, type }]. demandSlug optional (the publicSlug from /jobs/:slug).
// professionId / otherProfessionIds / otherProfessionText (Feature A, 2026-07-28): sent ONLY by a
// general (no-demand) apply's profession step — a NEW applicant picks 1 primary + up to 3 other
// professions from the ERP table, plus optional free-text for anything not listed. The server ignores
// these for a demand-attached apply (profession derived from the demand) or a profile-reuse apply.
// An account has at most one saved profile — the server looks it up itself by accountId and reuses its
// documents wholesale when one exists, accepting whatever fresh `docs` are staged on top (used both by
// a returning candidate uploading only the docs they're missing and by the "reuse my details" confirm).
// openToOtherRoles (optional, fresh-submission only): "consider me for other roles" consent captured
// at first application; the server defaults it to true when omitted.
// contactModes / contactModeOther (optional): preferred way(s) to be reached — collected in the
// post-OTP profile step for new candidates; omitted for returning ones.
// answers (optional, demand-attached apply only): the per-demand questionnaire responses,
// [{ questionId, value }] — checkbox value is 'true'/'false', date is the input/ISO value, number is
// the numeric string, text is the string. Ignored server-side when the demand has no questions.
export async function submitApplication({ docs, demandSlug, openToOtherRoles, contactModes, contactModeOther, answers, professionId, otherProfessionIds, otherProfessionText } = {}) {
  const { data } = await client.post('/public/applications', { docs, demandSlug, openToOtherRoles, contactModes, contactModeOther, answers, professionId, otherProfessionIds, otherProfessionText });
  return data; // { ok, applicationId }
}

// ── Dashboard (Phase 1: candidate pipeline Status/Matches/Documents tabs) ────
export async function getDashboardStatus() {
  const { data } = await client.get('/public/dashboard/status');
  return data;
}
export async function getDashboardMatches() {
  const { data } = await client.get('/public/dashboard/matches');
  return data.matches;
}
export async function applyToMatch(demandId) {
  const { data } = await client.post(`/public/dashboard/matches/${demandId}/apply`);
  return data;
}
export async function getDashboardDocuments() {
  const { data } = await client.get('/public/dashboard/documents');
  return data.documents;
}
// `type` is a built-in slot ('cv'|'passport'|'education'|'experience'|'police_clearance'|'other') or
// a `dt:<documentTypeId>` key — URL-encode it so the colon survives as a single path segment. The
// server 403s once the account is promoted (verified) — documents lock then and can only be replaced.
export async function addDashboardDocument(type, stageId) {
  const { data } = await client.post(`/public/dashboard/documents/${encodeURIComponent(type)}`, { stageId });
  return data.document;
}
export async function replaceDashboardDocument(documentId, stageId) {
  const { data } = await client.post(`/public/dashboard/documents/${documentId}/replace`, { stageId });
  return data.document;
}
// Same blob + object-URL approach as fetchPreviewObjectUrl (the preview route needs the bearer
// token, so a plain <a target="_blank"> can't carry it). Caller must URL.revokeObjectURL when done.
export async function fetchDashboardDocumentPreviewUrl(documentId) {
  const { data } = await client.get(`/public/dashboard/documents/${documentId}/preview`, { responseType: 'blob' });
  return URL.createObjectURL(data);
}

// ── Office-visit self-service booking (2026-07-25) ───────────────────────────
export async function getVisitInfo() {
  const { data } = await client.get('/public/dashboard/visit/info');
  return data;
}
export async function bookVisit(date, windowKey) {
  const { data } = await client.post('/public/dashboard/visit/book', { date, window: windowKey });
  return data;
}
// Same bearer-token-needs-a-blob reasoning as the document preview above.
export async function fetchVisitIcsUrl() {
  const { data } = await client.get('/public/dashboard/visit/calendar.ics', { responseType: 'blob' });
  return URL.createObjectURL(data);
}
